// src/stores/organizationStore.js
import { defineStore } from 'pinia'
import { collection } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { db } from '@/services/firebaseconfig'
import { organizationName } from '@/utils/crewAssignment'
import { isSelectableOrganization } from '@/utils/selectorEligibility'

export const useOrganizationStore = defineStore('organizationStore', {
  state: () => ({
    organizations: [], // [{ id, name, capabilities: [], ... }]
    isLoading: false,
    error: null,
    hasFetched: false,
    lastFetched: 0,
    _inFlight: null,
    _unsubscribe: null,
  }),

  getters: {
    selectableOrganizations: state => state.organizations.filter(isSelectableOrganization),
    // An organization's name by id, as every screen shows it (profile.name; src/utils/crewAssignment.js). A crew
    // pick stores the id (ticket E56U4u6tAqafSE19DWRz); '' for an id that names no organization.
    nameOf(state) {
      return id => {
        if (!id) return ''
        const org = state.organizations.find(o => o.id === id)
        return org ? organizationName(org) : ''
      }
    },

    // Unique, normalized capability list across all orgs
    capabilityOptions(state) {
      const set = new Set()

      for (const org of state.organizations) {
        // After normalization in fetchOrganizations, capabilities is always an array
        const caps = Array.isArray(org.capabilities) ? org.capabilities : []
        for (const c of caps) {
          if (!c) continue
          set.add(String(c).trim())
        }

        // REMOVED 2026-08-02 — org type/subtype were injected into the CAPABILITY
        // option list, so "Subcontractor", "Partner", "Internal" and "Install Crew"
        // all appeared as pickable abilities and could be persisted as capability
        // REQUIREMENTS from Calendar Settings. Type never grants ability
        // (Erik's ruling). Options now come only from real assigned capabilities.
      }

      return Array.from(set)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .map(v => ({ label: v, value: v }))
    },
  },

  actions: {
    async fetchOrganizations(opts = {}) {
      const { force = false } = opts

      if (this._inFlight) return this._inFlight
      if (!force && this._unsubscribe?.active) return

      this.isLoading = true
      this.error = null

      this._inFlight = (async () => {
        try {
          this._unsubscribe?.()
          await new Promise((resolve, reject) => {
            this._unsubscribe = onSnapshot(
              collection(db, 'organizations'),
              snap => {
                this.organizations = snap.docs.map(d => {
                  const raw = d.data()
                  return {
                    id: d.id,
                    // Normalize: nested `profile.*` first, flat fallback
                    name: raw.profile?.name || raw.name || '',
                    type: raw.profile?.type || raw.type || '',
                    subcontractorType:
                      raw.profile?.subcontractorType || raw.subcontractorType || null,
                    address: raw.profile?.address || raw.address || '',
                    phone: raw.profile?.phone || raw.phone || '',
                    email: raw.profile?.email || raw.email || '',
                    website: raw.profile?.website || raw.website || '',
                    // Capabilities: nested `capabilities.enabled` or flat array
                    capabilities: Array.isArray(raw.capabilities)
                      ? raw.capabilities
                      : raw.capabilities?.enabled || [],
                    // Status
                    active: raw.status?.active ?? raw.enabled !== false,
                    memberCount: raw.status?.memberCount || 0,
                    // Preserve raw for anything else
                    ...raw,
                  }
                })
                this.hasFetched = true
                this.lastFetched = Date.now()
                resolve()
              },
              err => {
                this.error = err
                reject(err)
              }
            )
          })
        } catch (e) {
          console.error('[organizationStore] fetchOrganizations failed', e)
          this.error = e
          this.organizations = []
          this.hasFetched = true
        } finally {
          this.isLoading = false
          this._inFlight = null
        }
      })()

      return this._inFlight
    },
  },
})
